import React, { useState } from 'react';
import { Button } from "antd";
import { areAddressesEqual } from '../utils/addressUtils';
import { ERC721_ABI } from '../contracts/external_contracts';
import { ethers } from 'ethers';

import './UrlInput.scss';

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const UrlInput = ({ onSubmit, userAddress, userSigner, harvestooor }) => {
  const [entries, setEntries] = useState([
    {
      url: '',
      error: null
    }
  ]);
  const [rechecking, setRechecking] = useState(false);

  const isFormValid = async () => {
    const urlHash = {};
    for(let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const url = entry.url;
      entry.error = "";

      if(urlHash[url]) {
        entry.error = "You've already entered this URL";
        continue;
      }

      if (url.split("/").length < 2) {
        entry.error = "Invalid NFT URL";
        continue;
      }

      const address = url.substring(url.lastIndexOf('/', url.lastIndexOf('/') - 1) + 1, url.lastIndexOf('/'));
      if (!/^0x[0-9a-fA-F]*$/.test(address)) {
        entry.error = "Could not read contract address from URL. Are you sure that's the correct URL?";
        continue;
      }

      const tokenId = url.substring(url.lastIndexOf('/') + 1);
      if (!Number.isInteger(Number(tokenId))) {
        entry.error = "Could not read token ID from URL. Are you sure that's the correct URL?";
        continue;
      }

      const nftContract = new ethers.Contract(address, ERC721_ABI, userSigner);
      try {
        const currentOwner = await nftContract.ownerOf(tokenId);
        if(areAddressesEqual(currentOwner, userAddress)) {
          entry.error = "Good news! You already own that NFT, so you can remove it from this list 😊";
          continue;
        } else if(!areAddressesEqual(currentOwner, harvestooor.address)) {
          entry.error = "Harvestooor does not own that NFT. Are you sure you sold it to Harvestooor?";
          continue;
        } 
      } catch (error) {
        entry.error = "Failed to check ownership. Harvestooor probably doesn't own that NFT."
        continue;
      }
      

      const previousOwner = await harvestooor.getPriorOwnerByToken(address, tokenId);
      if(!areAddressesEqual(previousOwner, userAddress)) {
        entry.error = "Your address is not the previous owner of this NFT. Are you sure you connected with the right address?";
        continue;
      }
      
      urlHash[url] = url;
    };

    setEntries([...entries]);

    if (entries.some(entry => entry.error)) {
      return false;
    }

    return true;
  }

  const handleAddClick = async () => {
    if(!(await isFormValid())) {
      return;
    }
    
    setEntries([...entries, {url: '', error: null}]);
  };

  const handleDeleteClick = index => {
    const newEntries = [...entries];
    newEntries.splice(index, 1);
    setEntries(newEntries);
  };

  const handleChange = (event, index) => {
    const newUrls = [...entries];
    newUrls[index].url = event.target.value;
    setEntries(newUrls);
  };

  const handleSubmit = async () => {
    if(!(await isFormValid())) {
      return;
    }

    const nfts = entries.map((entry) => {
      const url = entry.url;
      const address = url.substring(url.lastIndexOf('/', url.lastIndexOf('/') - 1) + 1, url.lastIndexOf('/'));
      const tokenId = url.substring(url.lastIndexOf('/') + 1);
      return { address, tokenId };
    });
    onSubmit(nfts);
  };

  const handleRecheckClick = async () => {
    setRechecking(true);
    await sleep(500);
    await isFormValid();
    setRechecking(false);
  }

  const hasErrors = entries.some(entry => entry.error);
  const errorCount = entries.filter(entry => entry.error).length;
  
  return (
    <div className="UrlInput">
      {entries.map((entry, index) => (
        <div
          className="input-container"
          key={index}
        >
          <div className="row">
            <input
              type="text"
              value={entry.url}
              onChange={(event) => handleChange(event, index)}
              style={entry.error ? { borderColor: 'tomato' } : {}}
              placeholder={!entry.url ? "Enter exchange URL (e.g. https://opensea.io/assets/ethereum/0x8943c7bac1914c9a7aba750bf2b6b09fd21037e0/7885)" : null}
            />
            { entries.length > 1 &&
              <span
                className="delete-button"
                onClick={() => handleDeleteClick(index)}
              >X</span>
            }
          </div>
          { entry.error && !rechecking &&
            <div className="row">
              <div className="error">{entry.error}</div>
            </div>
          }
        </div>
      ))}

      { rechecking &&
        <p>Rechecking...</p>
      }
      { hasErrors && !rechecking &&
        <div>
          <span>Correct {errorCount === 1 ? 'that error' : 'those errors'} and then click <i>Recheck</i>.</span>
          <Button
            shape="round"
            size="medium"
            type="primary"
            onClick={handleRecheckClick}
            style={{display: 'inline', marginLeft: '10px'}}
          >
            Recheck
          </Button>
        </div>
      }
      { !hasErrors && !rechecking &&
        <>
          <Button
            shape="round"
            size="medium"
            type="primary"
            onClick={handleAddClick}
          >
            Add Another NFT URL
          </Button>
          <Button 
            shape="round"
            size="medium"
            type="primary"
            disabled={hasErrors}
            onClick={handleSubmit}
          >
            Buy back my NFTs!
          </Button>
        </>
      }
    </div>
  );
};

export default UrlInput;